"use client";

import { useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";

// getProductDetail은 이미 RPC를 서버에서 2회 재시도한다. 그걸 뚫고 이 바운더리까지
// 오는 건 대부분 Supabase 게이트웨이의 일시적 네트워크 지연(하루 ~4건)으로, 몇 초 안에
// 스스로 풀린다(LOCARORA-WEB-4S). 그래서 에러 UI를 보이기 전에 딱 한 번만 조용히
// reload 한다 — sessionStorage 키가 1회 예산을 강제하므로 진짜 실패는 루프에 빠지지
// 않고 두 번째에 정상적으로 에러 UI로 떨어진다.
// (admin 레포 app/global-error.tsx의 `isAuthRecoverable` 원샷 reload와 동일한 패턴)
const RELOAD_KEY_PREFIX = "__productDetailReload:";

function canAttemptReload(pathname: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(`${RELOAD_KEY_PREFIX}${pathname}`) === null;
  } catch {
    // 프라이빗 모드/스토리지 차단 환경에서는 재시도 예산을 추적할 수 없으므로
    // 재시도하지 않고 곧장 에러 UI로 간다.
    return false;
  }
}

export default function ProductDetailError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  if (error.digest?.startsWith("NEXT_REDIRECT")) throw error;
  if (error.digest?.startsWith("NEXT_NOT_FOUND")) throw error;

  const t = useTranslations("common");
  const pathname = usePathname();
  // 렌더 시점에 "이번엔 reload로 끝낼지"를 알아야 에러 UI 깜빡임 없이 null을 반환할
  // 수 있으므로 읽기는 lazy initializer에서, 기록+reload는 effect에서 한다.
  const [willReload] = useState(() => canAttemptReload(pathname));

  useEffect(() => {
    if (willReload) {
      try {
        sessionStorage.setItem(`${RELOAD_KEY_PREFIX}${pathname}`, "1");
      } catch {
        // ignore — 아래 reload는 그대로 진행하고, 재실패 시 키가 없어도
        // canAttemptReload가 false를 돌려주는 환경이라 루프는 생기지 않는다.
      }
      window.location.reload();
      return;
    }

    // 리포팅은 실제로 에러 UI를 보여주는 경우에만 — reload로 회복되는 건은 노이즈다.
    Sentry.captureException(error, {
      tags: { boundary: "product-detail", digest: error.digest },
    });
  }, [error, pathname, willReload]);

  if (willReload) return null;

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="text-center space-y-4 max-w-md">
        <h1 className="text-xl font-semibold text-gray-900">{t("error")}</h1>
        <div className="flex gap-3 justify-center pt-2">
          <button
            onClick={() => (window.location.href = "/")}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
          >
            {t("nav.home")}
          </button>
          <button
            onClick={reset}
            className="rounded-lg bg-orange-600 px-4 py-2 text-sm text-white hover:bg-orange-700"
          >
            {t("retry")}
          </button>
        </div>
      </div>
    </div>
  );
}
